// --- Number, date, and ID formatting utilities ---

// Number formatting

/**
 * Formats a number with comma thousands separators and optional decimal precision.
 * @param {number|string} x - The number to format.
 * @param {number} [decimalPoint=0] - Number of decimal places.
 * @returns {string}
 * @example
 * numberWithCommas(1234567.89, 2); // "1,234,567.89"
 * 
 * 
		// export function numberWithCommas(x, decimalPoint = 0) {
		// 	let output = x;
		// 	if (decimalPoint !== 0) {
		// 		x = (x * 1).toFixed(decimalPoint);
		// 	}
		// 	x = x.toString().split(".");
		// 	output = x[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
		// 	if (typeof x[1] !== "undefined") output += "." + x[1];
		// 	return output;
		// }
 */
export const numberWithCommas = (x, decimalPoint = 0) => {
	if (isNaN(x)) return "0";
	let num = Number(x);
	if (decimalPoint > 0) num = num.toFixed(decimalPoint);
	const parts = num.toString().split(".");
	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	return parts.join(".");
};

/**
 * Formats a number with thousands separators, left-aligned, and optional decimal precision.
 * @param {number|string} input
 * @param {number} [decimalPoint=0]
 * @returns {string}
 * @example
 * thousandsSeparator(1234567.89, 2); // "1,234,567.89"
 * 
 * 
		// export function thousandsSeparator(input, decimalPoint = 0) {
		// 	var output = input;
		// 	if (parseFloat(input)) {
		// 		input = parseFloat(input).toFixed(decimalPoint).toString();
		// 		var parts = input.split(".");
		// 		parts[0] = parts[0]
		// 			.split("")
		// 			.reverse()
		// 			.join("")
		// 			.replace(/(\d{3})(?!$)/g, "$1,")
		// 			.split("")
		// 			.reverse()
		// 			.join("");
		// 		output = parts.join(".");
		// 	}
		// 	return output;
		// }
 */
export const thousandsSeparator = (input, decimalPoint = 0) => {
	let n = Number(input);
	if (isNaN(n)) return "0";
	n = n.toFixed(decimalPoint);
	const parts = n.split(".");
	parts[0] = parts[0]
		.split("")
		.reverse()
		.join("")
		.replace(/(\d{3})(?!$)/g, "$1,")
		.split("")
		.reverse()
		.join("");
	return parts.join(".");
};

/**
 * Formats a number with sign, symbol, thousands separator, decimal point, and unit.
 * @param {number} num - The number.
 * @param {number} [decimalPoint=1] - Decimal places.
 * @param {string|null} [symbol=null] - Currency or other symbol.
 * @param {string|null} [unit=null] - Unit to append.
 * @param {boolean} [sign=true] - Whether to show sign.
 * @returns {string}
 * @example
 * applyFormatToNumber(-2000.55, 2, "$", "USD", true); // "- $ 2,000.55 USD"
 * 
 * 
		// export const applyFormatToNumber = (
		// 	num,
		// 	decimalPoint = 1,
		// 	symbol = null,
		// 	unit = null,
		// 	sign = true
		// ) => {
		// 	let strTemp = sign ? (num >= 0 ? "+ " : "- ") : "";
		// 	strTemp += symbol ? symbol : "";
		// 	strTemp += " " + numberWithCommas(Math.abs(num), decimalPoint);
		// 	strTemp += unit ? " " + unit : "";
		// 	return strTemp;
		// };
 */
export const applyFormatToNumber = (num, decimalPoint = 1, symbol = null, unit = null, sign = true) => {
	if (typeof num !== "number") num = Number(num) || 0;
	// let strTemp = sign ? (num >= 0 ? "+ " : "- ") : "";
	let strTemp = sign ? (num > 0 ? "+ " : num < 0 ? "- " : "") : "";
	if (symbol) strTemp += symbol;
	strTemp += " " + numberWithCommas(Math.abs(num), decimalPoint);
	if (unit) strTemp += " " + unit;
	return strTemp.trim();
};

/**
 * Limits the decimal digits of a string value.
 * @param {string} value - String representing a number.
 * @param {number} [decimalLength=2] - Max decimal places allowed.
 * @returns {string}
 * @example
 * handleDecimalsOnValue("12.34567", 2); // "12.34"
 * 
 * 
		// export const handleDecimalsOnValue = (value, decimalLength = 2) => {
		// 	const regex = new RegExp(`([0-9]*[.,]?[0-9]{0,${decimalLength}})`);
		// 	const match = value.match(regex);
		// 	return match ? match[0] : "";
		// };
 */
export const handleDecimalsOnValue = (value, decimalLength = 2) => {
	if (typeof value !== "string") value = String(value ?? "");
	const regex = new RegExp(`([0-9]*[.,]?[0-9]{0,${decimalLength}})`);
	const match = value.match(regex);
	return match ? match[0] : "";
};

/**
 * Applies a "+" or "-" sign to a number.
 * @param {number} num
 * @returns {string}
 * @example
 * applySignToNumber(-50); // "-50"
 * applySignToNumber(50); // "+50"
 */
export const applySignToNumber = (num) => (num >= 0 ? "+" : "") + num;

// Date formatting

/**
 * Formats a date as a US short date string.
 * @param {Date|string|number} d
 * @returns {string}
 * @example
 * getShortDateString("2024-01-01"); // "1/1/2024"
 */
export const getShortDateString = (d) => {
	if (!d) return "";
	return new Date(d).toLocaleDateString("en-US");
};

/**
 * Formats a date as a US short date and time string.
 * @param {Date|string|number} d
 * @param {boolean} [newLine=true] - Whether to put a newline between date and time.
 * @returns {string}
 * @example
 * getShortDateTimeString("2024-01-01T12:34:56", false); // "1/1/2024 12:34:56 PM"
 */
export const getShortDateTimeString = (d, newLine = true) => {
	if (!d) return "";
	const date = new Date(d);
	let result = date.toLocaleDateString("en-US");
	result += newLine ? "\n" : " ";
	result += date.toLocaleTimeString("en-US");
	return result;
};

/**
 * Returns the current date/time as a compact string for filenames.
 * @returns {string}
 * @example
 * getDateTimeString(); // "2024_0704_153025"
 * 
 * 
		// export const getDateTimeString = () => {
		// 	const date = new Date();
		// 	const year = date.getFullYear();
		// 	const month = `${date.getMonth() + 1}`.padStart(2, "0");
		// 	const day = `${date.getDate()}`.padStart(2, "0");
		// 	const hour = `${date.getHours()}`.padStart(2, "0");
		// 	const minute = `${date.getMinutes()}`.padStart(2, "0");
		// 	const second = `${date.getSeconds()}`.padStart(2, "0");
		// 	return `${year}_${month}${day}_${hour}${minute}${second}`;
		// };
 */
export function getDateTimeString() {
	const date = new Date();
	const pad = (n) => n.toString().padStart(2, "0");
	return (
		date.getFullYear() +
		"_" +
		pad(date.getMonth() + 1) +
		pad(date.getDate()) +
		"_" +
		pad(date.getHours()) +
		pad(date.getMinutes()) +
		pad(date.getSeconds())
	);
}

/**
 * Highlights all occurrences of `needle` in `haystack` with HTML.
 * @param {string} needle - The substring to highlight.
 * @param {string} haystack - The full string to search.
 * @param {boolean} [reactDOM=true] - If true, outputs React-style span, else normal HTML span.
 * @returns {string}
 * @example
 * highlight("fox", "quick brown fox", false);
 */
export function highlight(needle, haystack, reactDOM = true) {
	if (typeof haystack !== "string" || typeof needle !== "string" || !needle) return haystack;
	const safeNeedle = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // escape regex chars
	const regex = new RegExp(safeNeedle, "gi");
	return haystack.replace(regex, (str) =>
		reactDOM
			? `<mark>${str}</mark>`
			: `<span style="background-color:yellow;font-weight:600;">${str}</span>`
	);
}
